import { css } from '@emotion/react';
import { color, spacing, typography } from '@uniquegood/realworld-studio-design';

type ArrowDirection = 'up' | 'down' | 'left' | 'right';

interface OnboardingTooltipProps {
  title: string;
  content: string;
  arrowDirection?: ArrowDirection;
  onPrevious?: () => void;
  onNext?: () => void;
  showPrevious?: boolean;
  nextButtonText?: string;
  previousButtonText?: string;
}

function OnboardingTooltip({
  title,
  content,
  arrowDirection = 'up',
  onPrevious,
  onNext,
  showPrevious = false,
  nextButtonText = '다음',
  previousButtonText = '이전'
}: OnboardingTooltipProps) {
  return (
    <div css={tooltipContainer(arrowDirection)}>
      <div css={contentContainer}>
        <div css={textGroup}>
          <div css={titleStyle}>{title}</div>
          <div css={contentStyle}>{content}</div>
        </div>
        <div css={buttonGroup}>
          {showPrevious && onPrevious && (
            <button type="button" css={previousButtonStyle} onClick={onPrevious}>
              {previousButtonText}
            </button>
          )}
          {onNext && (
            <button type="button" css={nextButtonStyle} onClick={onNext}>
              {nextButtonText}
            </button>
          )}
        </div>
      </div>
      <div css={arrowStyle(arrowDirection)} />
    </div>
  );
}

export default OnboardingTooltip;

const tooltipContainer = (direction: ArrowDirection) => css`
  position: relative;
  display: inline-block;
  ${direction === 'up' ? 'padding-top: 8px;' : ''}
  ${direction === 'down' ? 'padding-bottom: 8px;' : ''}
  ${direction === 'left' ? 'padding-left: 8px;' : ''}
  ${direction === 'right' ? 'padding-right: 8px;' : ''}
`;

const contentContainer = css`
  background: #7800FF;
  border-radius: 4px;
  padding: 12px;
  color: ${color.surface_default_default};
  box-shadow: 0px 0px 8px rgba(0, 0, 0, 0.2);
  min-width: 280px;
  max-width: 340px;
`;

const textGroup = css`
  margin-bottom: ${spacing.common.medium};
`;

const titleStyle = css`
  font-family: ${typography.family.primary};
  font-size: ${typography.size.s3};
  font-weight: ${typography.weight.bold};
  line-height: 20px;
  margin-bottom: 4px;
  color: ${color.surface_default_default};
`;

const contentStyle = css`
  font-family: ${typography.family.primary};
  font-size: ${typography.size.s2};
  font-weight: ${typography.weight.regular};
  line-height: 20px;
  color: ${color.surface_default_default};
  white-space: pre-line;
`;

const buttonGroup = css`
  display: flex;
  justify-content: flex-end;
  gap: ${spacing.common.small};
`;

const buttonBase = css`
  border: none;
  border-radius: 8px;
  padding: 4px 16px;
  font-family: ${typography.family.primary};
  font-size: ${typography.size.s2};
  font-weight: ${typography.weight.medium};
  line-height: 20px;
  cursor: pointer;
  height: 28px;
  transition: all 0.2s ease;
`;

const previousButtonStyle = css`
  ${buttonBase}
  background: rgba(255, 255, 255, 0.2);
  color: ${color.surface_default_default};
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const nextButtonStyle = css`
  ${buttonBase}
  background: ${color.surface_default_default};
  color: #4A41FF;
  
  &:hover {
    background: rgba(255, 255, 255, 0.9);
  }
`;

const arrowStyle = (direction: ArrowDirection) => css`
  position: absolute;
  width: 0;
  height: 0;
  
  ${direction === 'up' && css`
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-bottom: 8px solid #7800FF;
  `}
  
  ${direction === 'down' && css`
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-top: 8px solid #7800FF;
  `}
  
  ${direction === 'left' && css`
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    border-top: 8px solid transparent;
    border-bottom: 8px solid transparent;
    border-right: 8px solid #7800FF;
  `}
  
  ${direction === 'right' && css`
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    border-top: 8px solid transparent;
    border-bottom: 8px solid transparent;
    border-left: 8px solid #7800FF;
  `}
`;